import {createNamespacedHelpers} from 'vuex'
import {namespace} from '../vuexstore'
import {default as cardStore, namespace as cardNamespace} from '../vuexstore/modules/giftcard'
import { mapGetters } from 'vuex'
const { mapGetters: mapCardGetters, mapActions: mapCardActions } = createNamespacedHelpers([namespace, cardNamespace].join('/'))
import { getFirstErrorMessage } from '~/lib/axios'
import axios from 'axios'
import { required, email } from 'vuelidate/lib/validators'

const EventBus = window.VueEventBus

export default {
  computed: {
    ...mapGetters({
      user: 'authUser',
    }),
    ...mapCardGetters(Object.keys(cardStore.getters)),

    ownGiftcards() {
      return this.giftcards.filter(x => x.role === 'recipient')
    },

    sentGiftcards() {
      return this.giftcards.filter(x => x.role === 'giver')
    },
  },

  data: () => ({
    giftcards: [],

    giftcard: {
      number: null,
      cvv: null,
    },

    resendEmail: null,

    areGiftcardsLoaded: false,

    isChecking: {},
    isResending: {},
    isSendingCVV: {},
    isClaiming: {},
    isArchiving: {},
    isAdding: false,

    checkingErrorMessage: null,
    resendErrorMessage: null,
    addingErrorMessage: null,
    archivingErrorMessage: null,
    claimErrorMessage: null,
  }),

  validations: {
    giftcard: {
      number: {
        required,
      },
      cvv: {
        required,
      },
    },
    resendEmail: {
      required,
      email,
    },
  },

  methods: {
    ...mapCardActions(Object.keys(cardStore.actions)),

    canShowBalance(giftcard) {
      return !(giftcard.role === 'giver' && giftcard.state !== 'given')
    },

    canShowNumber(giftcard) {
      return giftcard.state !== 'given'
    },

    canShowRecipient(giftcard) {
      return giftcard.role === 'giver' && giftcard.recipient
    },

    canShowGiver(giftcard) {
      return giftcard.role === 'recipient' && giftcard.giver && giftcard.giver.data.id !== this.user.id
    },

    isForMyself(giftcard) {
      return giftcard.role === 'recipient' && giftcard.giver && giftcard.giver.data.id === this.user.id
    },

    isClaimable(giftcard) {
      return giftcard.role === 'recipient' && giftcard.state === 'given'
    },

    isClaimableForMyself(giftcard) {
      return giftcard.role === 'giver' && giftcard.state === 'given'
    },

    isCodeRecoverable(giftcard) {
      return giftcard.role === 'recipient' && giftcard.state !== 'given'
    },

    isResendable(giftcard) {
      return giftcard.state === 'given' && giftcard.role === 'giver'
    },

    isArchivable(giftcard) {
      return giftcard.state !== 'given'
    },
    emit(eventName, ...payload) {
      this.$emit(eventName, ...payload)
      EventBus.$emit(eventName, ...payload)
    },
    emitError(eventName, e, ...payload) {
      const error = getFirstErrorMessage(e)
      this.emit(eventName, error, e, ...payload)
      return error
    },
    async load() {
      try {
        const { data: { data: giftcards } } = await axios.get(`${this.config.apiUri}/customer/giftcard?page_size=999`)
        this.giftcards = giftcards
        this.areGiftcardsLoaded = true
        this.emit('giftcards.loaded', giftcards)
       } catch (e) {
        // this.areGiftcardsLoaded = false
        this.emitError('giftcards.load.error', e)
      }
    },

    async claim(giftcard) {
      try {
        this.$set(this.isClaiming, giftcard.id, true)
        await axios.post(`${this.config.apiUri}/customer/giftcard/${giftcard.id}/claim`)
        this.claimErrorMessage = null
        this.emit('giftcard.claimed', giftcard)
        await this.load()
      } catch (e) {
        const error = this.emitError('giftcard.claim.error', e, giftcard)
        this.claimErrorMessage = error
      } finally {
        this.$set(this.isClaiming, giftcard.id, false)
      }
    },

    async resend(giftcard) {
      try {
        const payload = {
          // It's better to use the gift card's own recipient email rather than the global this.resendEmail property
          // Unless set before sending, there'a chance that this.resendEmail is null or storing email of the previously
          // sent recipient as this property is being set just after sending.
          // In case a custom email recipient is required it needs to be passed rather as a second param than using a
          // global property
          // email: this.resendEmail
          email: giftcard.recipient.data.email
        }
        this.$set(this.isResending, giftcard.id, true)
        await axios.post(`${this.config.apiUri}/customer/giftcard/${giftcard.id}/resend`, payload)
        this.resendEmail = giftcard.recipient.data.email
        this.resendErrorMessage = null
        this.emit('giftcard.resent', giftcard)
      } catch (e) {
        const error = this.emitError('giftcard.resend.error', e, giftcard)
        this.resendErrorMessage = error
      } finally {
        this.$set(this.isResending, giftcard.id, false)
      }
    },

    async add() {
      const payload = {
        number: this.giftcard.number,
        cvv: this.giftcard.cvv,
      }
      try {
        this.isAdding = true
        const { data: { data: giftcard } }  = await axios.post(`${this.config.apiUri}/customer/giftcard/add`, payload)
        this.addingErrorMessage = null
        this.emit('giftcard.added', giftcard)
        await this.load()
      } catch (e) {
        const error = this.emitError('giftcard.add.error', e, payload)
        this.addingErrorMessage = error
      } finally {
        this.isAdding = false
      }
    },

    async archive(giftcard) {
      try {
        this.$set(this.isArchiving, giftcard.id, true)
        await axios.post(`${this.config.apiUri}/customer/giftcard/${giftcard.id}/archive`)
        this.archivingErrorMessage = null
        this.emit('giftcard.archived', giftcard)
        await this.load()
      } catch (e) {
        const error = this.emitError('giftcard.archive.error', e, giftcard)
        this.archivingErrorMessage = error
      } finally {
        this.$set(this.isArchiving, giftcard.id, false)
      }
    },

    async check(card) {
      const {number, cvv, id = number} = card
      const payload = {number, cvv}
      try {
        this.$set(this.isChecking, id, true)
        const { data: { data: giftcard } }  = await axios.post(`${this.config.apiUri}/customer/giftcard/check`, payload)
        // this.giftcard = giftcard
        this.emit('giftcard.checked', giftcard)
        this.checkingErrorMessage = null
        this.$set(this.isChecking, id, false)
        return giftcard
      } catch (e) {
        const error = this.emitError('giftcard.check.error', e, card)
        this.checkingErrorMessage = error
        this.$set(this.isChecking, id, false)
      }
    },
  },
}

